import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import React, {
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { UserWithFavorites } from '../models';
import { fbAuth } from '../services/firebase-client';
import { trpc } from '../trpc';

type AuthStateSetEvent = { type: 'SET'; user: UserWithFavorites };
type AuthStateResetEvent = { type: 'RESET' };
type AuthStateEvent = AuthStateSetEvent | AuthStateResetEvent;

type AuthState =
  | { value: 'loading'; user: null }
  | { value: 'loggedIn'; user: UserWithFavorites }
  | { value: 'loggedOut'; user: null };

const authStateReducer: Reducer<AuthState, AuthStateEvent> = (_, action) => {
  switch (action.type) {
    case 'SET':
      return {
        value: 'loggedIn',
        user: action.user,
      };
    case 'RESET':
      return {
        value: 'loggedOut',
        user: null,
      };
  }
};

type SignInWithPasswordData = {
  email: string;
  password: string;
};

type SignUpData = SignInWithPasswordData & {
  name: string;
};

type AuthContextState = {
  state: {
    user: AuthState['user'];
    status: AuthState['value'];
    isLoggedIn: boolean;
  };
  signUp: (data: SignUpData) => Promise<void>;
  signInWithPassword: (data: SignInWithPasswordData) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const noop = () => Promise.resolve();

const AuthContext = React.createContext<AuthContextState>({
  state: {
    status: 'loading',
    user: null,
    isLoggedIn: false,
  },
  signUp: noop,
  signInWithPassword: noop,
  signInWithGoogle: noop,
  signOut: noop,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();

  const [state, dispatch] = useReducer(authStateReducer, {
    value: 'loading',
    user: null,
  });

  const userNameRef = useRef<string | null>(null);

  const { mutateAsync: login } = trpc.auth.login.useMutation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(fbAuth, async (user) => {
      if (user) {
        if (userNameRef.current) {
          await updateProfile(user, { displayName: userNameRef.current });
          userNameRef.current = null;
        }

        try {
          const res = await login({ token: await user.getIdToken() });

          dispatch({ type: 'SET', user: res });
        } catch (err) {
          console.error(err);
          // TODO: handle error
          await signOut(fbAuth);
        }
      } else {
        dispatch({ type: 'RESET' });
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  trpc.auth.me.useQuery(
    {},
    {
      onSuccess: (res) => {
        dispatch({ type: 'SET', user: res });
      },
      onError: async (err) => {
        console.error(err);
        if (!state.user) {
          await signOut(fbAuth);
        }
      },
      enabled: state.value === 'loggedIn',
    },
  );

  // useQuery(
  //   'auth/me',
  //   async () => {
  //     try {
  //       const res = await apiRequest('auth/me', null);
  //       dispatch({ type: 'SET', user: res.data });
  //     } catch (err) {
  //       console.error(err);
  //       if (!state.user) {
  //         await signOut(fbAuth);
  //       }
  //     }
  //   },
  //   { enabled: state.value === 'loggedIn' },
  // );

  const signUp = async (data: SignUpData) => {
    userNameRef.current = data.name;
    await createUserWithEmailAndPassword(fbAuth, data.email, data.password);
  };

  const onSignInWithPassword = async (data: SignInWithPasswordData) => {
    await signInWithEmailAndPassword(fbAuth, data.email, data.password);
  };

  const onSignInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(fbAuth, provider);
  };

  const onSignOut = useCallback(async () => {
    await signOut(fbAuth);
    router.replace('/');
  }, [router]);

  const value: AuthContextState = useMemo(
    () => ({
      state: {
        status: state.value,
        user: state.user,
        isLoggedIn: state.value === 'loggedIn',
      },
      signUp,
      signInWithPassword: onSignInWithPassword,
      signInWithGoogle: onSignInWithGoogle,
      signOut: onSignOut,
    }),
    [onSignOut, state.user, state.value],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthCtx = (): AuthContextState => {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthCtx must be used within an AuthProvider');
  }

  return context;
};

export const useAuthState = (): AuthContextState['state'] => {
  return useAuthCtx().state;
};

export const useAuthUser = (): UserWithFavorites | null => {
  return useAuthCtx().state.user;
};
